import React, { Suspense } from 'react';
import PhoneImage from 'assets/images/servicesLanding/fertility-outreach-phone.png';
import WomanTexting from 'assets/images/woman-texting.png';
import useResize from 'hooks/useResize';

import { Flex } from 'shared/styledComponents';
import styled from 'styled-components';
import theme from 'theme';

const BannerAdContainer = styled.div`
  position: relative;
  width: 100%;
`;

const BannerAdContentWrapper = styled(Flex)`
  overflow: hidden;
  width: 62.5rem;
  height: 15.625rem;
  background-color: ${({ theme: { colors } }): string => colors.forPlum};
  font-family: ${({ theme: { fonts } }): string => fonts.brandonGrotesqueRegular};
  font-size: 1.15rem;
  line-height: 2rem;
  align-items: center;
  color: ${({ theme: { colors } }): string => colors.white};
  gap: 1rem;
  padding-right: 1.5rem;
  @media ${theme.devices.mobile} {
    width: 100%;
    height: 100%;
    flex-direction: column;
    padding: 1.5rem 1.5rem 2.5rem 1.5rem;
`;

const BannerImageWrapper = styled.img`
  width: 16rem;
  height: 16rem;
  @media ${theme.devices.mobile} {
    width: 100%;
    height: 100%;
  }
`;

const TextContentWrapper = styled.div`
  margin-right: 2rem;
  @media ${theme.devices.mobile} {
    margin-right: 0;
    width: 100%;
  }
`;

const TitleTextWrapper = styled.div`
  font-family: ${({ theme: { fonts } }): string => fonts.brandonGrotesqueMedium};
  font-size: 1.5rem;
  width: 18rem;
  margin-bottom: 1rem;
  @media ${theme.devices.mobile} {
    font-size: 1.25rem;
    width: 80%;
  }
`;

const BoldText = styled.span`
  font-family: ${({ theme: { fonts } }): string => fonts.brandonGrotesqueMedium};
  font-weight: bold;
`;

const SubtitleTextWrapper = styled(Flex)`
  position: relative;
  justify-content: flex-end;
  z-index: 3;
`;

const SubtitleBackground = styled.div`
  position: absolute;
  bottom: 0;
  right: -5px;
  width: 12.5rem;
  height: 1rem;
  background-color: ${({ theme: { colors } }): string => colors.forPlum};
  -webkit-transform: skewX(15deg);
  --webkit-filter: brightness(0.6);
  --moz-filter: brightness(0.6);
  --o-filter: brightness(0.6);
  --ms-filter: brightness(0.6);
  filter: brightness(0.6);
  z-index: 1;
  @media ${theme.devices.mobile} {
    width: 10.5rem;
`;

const BannerAdButtonWrapper = styled(Flex)`
  @media ${theme.devices.mobile} {
    justify-content: center;
    width: 100%;
    position: relative;
  }
`;

const BannerAdButton = styled.button`
  font-family: ${({ theme: { fonts } }): string => fonts.brandonGrotesqueMedium};
  font-size: 1rem;
  text-transform: uppercase;
  font-weight: bold;
  background-color: ${({ theme: { colors } }): string => colors.white};
  color: ${({ theme: { colors } }): string => colors.black};
  border: none;
  padding: 1.25rem;
  width: 200px;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  position: relative;
  z-index: 2;
  :hover {
    background-color: ${({ theme: { colors } }): string => colors.forLavender};
  }
`;

const BannerAdBackground = styled.div`
  position: absolute;
  width: 100%;
  height: 3rem;
  bottom: -0.625rem;
  left: 0;
  -webkit-transform: skewX(-10deg);
  z-index: 1;
  background-color: ${({ theme: { colors } }): string => colors.forYellow};
`;

const VeevaCodeWrapper = styled(Flex)`
  position: absolute;
  justify-content: flex-end;
  color: ${({ theme: { colors } }): string => colors.white};
  width: 100%;
  right: 0;
  bottom: 0;
  margin-right: 1.5rem;
  margin-bottom: 0.5rem;
  font-family: ${({ theme: { fonts } }): string => fonts.brandonGrotesqueRegular};
  font-size: 0.75rem;
  @media ${theme.devices.mobile} {
    margin-right: 0;
    justify-content: center;
  }
`;

const BannerAd = (): JSX.Element => {
  const { isDesktop } = useResize();

  return (
    <BannerAdContainer>
      <BannerAdContentWrapper>
        <Suspense fallback={<div>Loading...</div>}>
          {isDesktop ? (
            <BannerImageWrapper
              src={PhoneImage}
              alt="graphic model of a yellow cell phone that reads Fertility Outreach"
            />
          ) : (
            <BannerImageWrapper
              src={WomanTexting}
              alt="woman in a knit sweater at her office texting on her cell phone"
            />
          )}
        </Suspense>
        <TextContentWrapper>
          <TitleTextWrapper>
            <div style={{ display: 'flex' }}>
              <h3>Personalized support</h3>
            </div>
            <SubtitleTextWrapper>
              <h3 style={{ zIndex: 2 }}>at your fingertips</h3>
              <SubtitleBackground />
            </SubtitleTextWrapper>
          </TitleTextWrapper>
          <p>
            Questions are bound to come up as you go through your fertility journey—but{' '}
            <BoldText>Fertility Outreach</BoldText> is here to help you every step of the way. Our fertility coaches
            offer real-time personalized guidance and support right to your phone.
          </p>
        </TextContentWrapper>
        <BannerAdButtonWrapper>
          <a href={'https://outreach.fertilityoutloud.com/'} rel={'noopener noreferrer'} target={'_blank'}>
            <BannerAdButton>SIGN UP TO TEXT</BannerAdButton>
          </a>
          {!isDesktop && <BannerAdBackground />}
        </BannerAdButtonWrapper>
      </BannerAdContentWrapper>
      <VeevaCodeWrapper>
        <p>US-RMMH-2300266</p>
      </VeevaCodeWrapper>
    </BannerAdContainer>
  );
};

export default BannerAd;
